import { callProxy } from './supabase';

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  avatar?: string;
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  roles?: AdminRole[];
  permissions?: string[];
}

export interface AdminRole {
  id: string;
  role_name: string;
  display_name: string;
  description?: string;
}

export interface AdminPermission {
  id: string;
  permission_key: string;
  display_name: string;
  category?: string;
  description?: string;
}

export interface CreateAdminUserParams {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  role_ids: string[];
}

export interface UpdateAdminUserParams {
  username?: string;
  email?: string;
  full_name?: string;
  status?: 'active' | 'inactive' | 'suspended';
  role_ids?: string[];
  password?: string; // Thêm trường password
}

/**
 * Lấy danh sách admin users
 */
export async function getAdminUsers(params?: {
  current?: number;
  pageSize?: number;
  username?: string;
  email?: string;
  status?: string;
  role?: string;
}) {
  const { current = 1, pageSize = 10, username, email, status, role } = params || {};

  try {
    // Gọi Proxy lấy dữ liệu thô
    const allUsers = await callProxy({
      action: 'select',
      table: 'admin_users',
      select: '*, admin_user_roles(admin_roles(*))'
    });

    // Filter local
    let filtered = allUsers || [];
    if (username) filtered = filtered.filter((u: any) => u.username.toLowerCase().includes(username.toLowerCase()));
    if (email) filtered = filtered.filter((u: any) => u.email.toLowerCase().includes(email.toLowerCase()));
    if (status && status !== 'all') filtered = filtered.filter((u: any) => u.status === status);
    // Note: Filtering by role is complex with current proxy setup and nested data,
    // so it's omitted for now or would require a more sophisticated proxy query.

    const total = filtered.length;
    const from = (current - 1) * pageSize;
    const paginated = filtered.slice(from, from + pageSize);

    // Format data với roles
    const formattedData = paginated?.map((user: any) => {
      const userData = {
        ...user,
        roles: user.admin_user_roles?.map((ur: any) => ur.admin_roles) || [],
      };

      // Mock last_login_at cho cow_admin từ localStorage
      if (user.username === 'cow_admin') {
        const lastLogin = localStorage.getItem('cow_last_login');
        if (lastLogin) {
          userData.last_login_at = lastLogin;
        }
      }

      return userData;
    }) || [];

    return {
      data: formattedData,
      success: true,
      total: total,
    };
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return {
      data: [],
      success: false,
      total: 0,
    };
  }
}

/**
 * Lấy tất cả roles
 */
export async function getAllRoles() {
  try {
    const data = await callProxy({
      action: 'select',
      table: 'admin_roles',
      select: '*'
    });

    return {
      data: data || [],
      success: true,
    };
  } catch (error) {
    console.error('Error fetching roles:', error);
    return {
      data: [],
      success: false,
    };
  }
}

/**
 * Lấy permissions của một role
 */
export async function getRolePermissions(roleId: string) {
  try {
    const data = await callProxy({
      action: 'select',
      table: 'admin_role_permissions',
      select: 'admin_permissions(*)',
      eqField: 'role_id',
      eqValue: roleId
    });

    return {
      data: data?.map((rp: any) => rp.admin_permissions) || [],
      success: true,
    };
  } catch (error) {
    console.error('Error fetching role permissions:', error);
    return {
      data: [],
      success: false,
    };
  }
}

/**
 * Tạo admin user mới
 */
export async function createAdminUser(params: CreateAdminUserParams) {
  try {
    // Lưu password plain text (KHÔNG hash)
    // Note: Trong production thực tế, nên hash password
    const userData = await callProxy({
      action: 'insert',
      table: 'admin_users',
      data: {
        username: params.username,
        email: params.email,
        password_hash: params.password, // Lưu plain text
        full_name: params.full_name,
        status: 'active',
      }
    });

    if (!userData) {
      throw new Error('Failed to create user.');
    }

    // Gán roles
    if (params.role_ids && params.role_ids.length > 0) {
      for (const roleId of params.role_ids) {
        await callProxy({
          action: 'insert',
          table: 'admin_user_roles',
          data: {
            user_id: userData.id,
            role_id: roleId,
          }
        });
      }
    }
    return {
      success: true,
      data: userData,
    };
  } catch (error: any) {
    console.error('Error creating admin user:', error);
    return {
      success: false,
      message: error.message || 'Tạo tài khoản thất bại',
    };
  }
}

/**
 * Cập nhật admin user
 */
export async function updateAdminUser(userId: string, params: UpdateAdminUserParams) {
  try {
    const updateData: any = {};

    if (params.username) updateData.username = params.username;
    if (params.email) updateData.email = params.email;
    if (params.full_name !== undefined) updateData.full_name = params.full_name;
    if (params.status) updateData.status = params.status;
    if (params.password) updateData.password_hash = params.password; // Cập nhật password plain text

    const userData = await callProxy({
      action: 'update',
      table: 'admin_users',
      data: updateData,
      eqField: 'id',
      eqValue: userId
    });

    // Cập nhật roles nếu có
    if (params.role_ids) {
      // Xóa roles cũ
      await callProxy({
        action: 'delete',
        table: 'admin_user_roles',
        eqField: 'user_id',
        eqValue: userId
      });

      // Thêm roles mới
      if (params.role_ids.length > 0) {
        for (const roleId of params.role_ids) {
          await callProxy({
            action: 'insert',
            table: 'admin_user_roles',
            data: {
              user_id: userId,
              role_id: roleId,
            }
          });
        }
      }
    }
    return {
      success: true,
      data: userData,
    };
  } catch (error: any) {
    console.error('Error updating admin user:', error);
    return {
      success: false,
      message: error.message || 'Cập nhật tài khoản thất bại',
    };
  }
}

/**
 * Xóa admin user
 */
export async function deleteAdminUser(userId: string) {
  try {
    await callProxy({
      action: 'delete',
      table: 'admin_users',
      eqField: 'id',
      eqValue: userId
    });
    return {
      success: true,
    };
  } catch (error: any) {
    console.error('Error deleting admin user:', error);
    return {
      success: false,
      message: error.message || 'Xóa tài khoản thất bại',
    };
  }
}

/**
 * Lấy permissions của user hiện tại
 */
export async function getCurrentUserPermissions(userId: string) {
  try {
    const data = await callProxy({
      action: 'select',
      table: 'admin_user_roles',
      select: 'admin_roles(admin_role_permissions(admin_permissions(*)))',
      eqField: 'user_id',
      eqValue: userId
    });

    // Flatten permissions
    const permissions = new Set<string>();
    data?.forEach((ur: any) => {
      ur.admin_roles?.admin_role_permissions?.forEach((rp: any) => {
        if (rp.admin_permissions?.permission_key) {
          permissions.add(rp.admin_permissions.permission_key);
        }
      });
    });

    return {
      data: Array.from(permissions),
      success: true,
    };
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    return {
      data: [],
      success: false,
    };
  }
}

/**
 * Lấy role của user hiện tại (dựa vào username trong localStorage)
 */
export async function getCurrentUserRole() {
  try {
    // Lấy username từ localStorage
    const currentUsername = localStorage.getItem('cow_current_username');

    if (!currentUsername) {
      // Fallback: Hardcode cow_admin là super_admin
      return {
        data: 'super_admin',
        success: true,
      };
    }

    const data = await callProxy({
      action: 'select',
      table: 'admin_users',
      select: 'admin_user_roles(admin_roles(role_name))',
      eqField: 'username',
      eqValue: currentUsername
    });

    // Lấy role đầu tiên
    const userRoles = (data as any)?.[0]?.admin_user_roles as any;
    const roleName = userRoles?.[0]?.admin_roles?.role_name;

    return {
      data: roleName || null,
      success: true,
    };
  } catch (error) {
    console.error('Error fetching current user role:', error);
    // Fallback: Hardcode cow_admin là super_admin
    return {
      data: 'super_admin',
      success: true,
    };
  }
}
